import { useState } from 'react';
import { Link } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { z } from 'zod';
import { resetPasswordSchema as forgotPasswordSchema } from '@fixtyer/shared';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

const API_URL = import.meta.env.VITE_API_URL || '';

type ForgotPasswordForm = z.infer<typeof forgotPasswordSchema>;

export function ForgotPasswordPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordForm>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data: ForgotPasswordForm) => {
    setError(null);
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: data.email }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send reset email');
      }

      setIsSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send reset email');
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-[calc(100vh-16rem)] flex items-center justify-center py-12 px-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl shadow-brand-900/5 border border-surface-200 p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-brand-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-brand-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h1 className="font-display text-2xl font-bold text-brand-950 mb-2">
                Check your email
              </h1>
              <p className="text-surface-800 text-sm leading-relaxed">
                If an account exists with that email, we've sent you instructions to reset your password.
              </p>
            </div>

            <div className="bg-brand-50 border border-brand-200 rounded-xl p-4 mb-6">
              <p className="text-sm text-brand-900 leading-relaxed">
                <strong className="font-semibold">Didn't receive the email?</strong>
                <br />
                Check your spam folder or try again in a few minutes.
              </p>
            </div>

            <Link
              to="/login"
              className="block w-full text-center py-3 px-4 rounded-xl border border-surface-300 text-brand-600 hover:bg-surface-50 font-medium transition-colors"
            >
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-16rem)] flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="font-display text-3xl font-bold text-brand-950">
            Forgot your password?
          </h1>
          <p className="mt-2 text-surface-800">
            No worries, enter your email to receive reset instructions
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl shadow-brand-900/5 border border-surface-200 p-8">
          {error && (
            <div className="mb-6 p-4 bg-error-500/10 border border-error-500/20 rounded-xl text-error-600 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <Input
              label="Email address"
              type="email"
              autoComplete="email"
              placeholder="Enter your email"
              error={errors.email?.message}
              {...register('email')}
            />

            <Button type="submit" fullWidth loading={isLoading}>
              Send reset link
            </Button>
          </form>

          <div className="mt-6 text-center text-sm">
            <Link to="/login" className="text-brand-600 hover:text-brand-700 font-medium">
              ← Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
